import { createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState, MapResponseAction, hasMapResponse, mapSelector, MarkerType, MarkerDecorationType } from "..";

import visrezMarkerDecorationSVG from '../../media/markers/decoration-visrez.svg?inline';

export function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

export function dataUrlToSvg(dataUrl: string): string {
  if (dataUrl.startsWith('data:image/svg+xml;base64,')) {
    const base64Data = dataUrl.replace(/^data:image\/svg\+xml;base64,/, '');
    return atob(base64Data);
  }
  if (dataUrl.startsWith('data:image/svg+xml,')) {
    return decodeURIComponent(dataUrl.replace(/^data:image\/svg\+xml,/, ''));
  }
  throw new Error('Invalid SVG data URL format');
}

export const visrezDefaultMarkerDecoration: MarkerDecorationType = {
  id: 'default',
  base64: svgToDataUrl(visrezMarkerDecorationSVG),
};

type MarkersState = {
  list: MarkerType[];
  decorations: MarkerDecorationType[];
};

function updateReducer(state: MarkersState, { payload }: { payload: Partial<MarkerType> }): MarkersState {
  return {
    ...state,
    list: state.list
      .map((marker) => (
        marker.id === payload.id
          ? { ...marker, ...payload }
          : marker
      ))
  };
}

function updateDecorationReducer(state: MarkersState, { payload }: { payload: Partial<MarkerDecorationType> }): MarkersState {
  return {
    ...state,
    decorations: state.decorations
      .map((decoration) => (
        decoration.id === payload.id
          ? { ...decoration, ...payload }
          : decoration
      ))
  };
}

export const markersSlice = createSlice({
  name: 'markers',
  initialState: {
    list: [] as MarkerType[],
    decorations: [visrezDefaultMarkerDecoration],
  },
  reducers: {
    set: (state, { payload }) => (
      { ...state, list: payload }
    ),
    add: (state, { payload }) => (
      { ...state, list: [...state.list, payload] }
    ),
    remove: (state, { payload }) => (
      { ...state, list: state.list.filter((marker) => marker.id !== payload) }
    ),
    // Could use update, but this one adds a undo/redo action
    resize: (state, { payload: { id, size } }) => (
      {
        ...state,
        list: state.list.map((marker) => marker.id === id ? { ...marker, size: size } : marker),
      }
    ),
    update: updateReducer,
    internalUpdateMarker: updateReducer, // Duplicated to allow using it without it being undoable
    internalUpdateMarkersBulk: (state, { payload }) => (
      {
        ...state,
        list: state.list.map((marker) => {
          const update = payload.find((m: MarkerType) => m.id === marker.id);
          return { ...marker, ...(update ?? {}) };
        })
      }),
    addDecoration: (state, { payload }) => (
      { ...state, decorations: [...state.decorations, payload] }
    ),
    setDecorations: (state, { payload }) => (
      { ...state, decorations: payload }
    ),
    updateDecoration: updateDecorationReducer,
    internalUpdateDecoration: updateDecorationReducer, // Duplicated to allow using it without it being undoable
    internalUpdateDecorationsBulk: (state, { payload }) => (
      {
        ...state,
        decorations: state.decorations.map((decoration) => {
          const update = payload.find((d: MarkerDecorationType) => d.id === decoration.id);
          return { ...decoration, ...(update ?? {}) };
        })
      }),
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action): action is MapResponseAction => hasMapResponse(action),
      (state, { payload }) => {
        state.list = payload.markers;
        state.decorations = [visrezDefaultMarkerDecoration, ...payload.markerDecorations];
      }
    );
  },
});

const {
  set, add, remove, update, resize, addDecoration, setDecorations, updateDecoration,
} = markersSlice.actions;
export { set as setMarkers, add as addMarker, remove as removeMarker, update as updateMarker, resize as resizeMarker, addDecoration, setDecorations, updateDecoration };

export const markersSelector = (state: RootState) => state.undoable.present.markers.list;

// Return the default marker for the map.
export const mapMarkersSelector = createSelector(
  mapSelector,
  markersSelector,
  (map, markers) => {
    return markers.find((m) => m.id === map.markerId) ||
      map.defaultMarkers;
  }
);

export const markerDecorationsSelector = (state: RootState) => state.undoable.present.markers.decorations;
